import type { PostHogInterface } from "posthog-js";
import { PostHogProvider as PHProvider, usePostHog } from "posthog-js/react";
import { createContext, type ReactNode, useContext, useEffect, useRef, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { type ConsentStatus, useCookieConsentStore } from "@/stores/cookieConsentStore";

function resolveConfiguredValue(runtimeValue: string | undefined, buildValue: string | undefined) {
  const trimmedRuntimeValue = runtimeValue?.trim();
  if (trimmedRuntimeValue) {
    return trimmedRuntimeValue;
  }

  return buildValue?.trim() ?? "";
}

// Runtime config takes precedence, but blank runtime values should fall back to
// the build-time config instead of silently disabling analytics.
const posthogKey = resolveConfiguredValue(
  window.__ENV__?.VITE_PUBLIC_POSTHOG_KEY,
  import.meta.env.VITE_PUBLIC_POSTHOG_KEY,
);
const posthogHost =
  resolveConfiguredValue(
    window.__ENV__?.VITE_PUBLIC_POSTHOG_HOST,
    import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
  ) || "https://eu.i.posthog.com";

/**
 * The reset-password route carries a one-time token in its query string, and a seat invite link (/redeem) a redeem
 * code; mask them (alongside PostHog's default advertising IDs) in captured URLs. /redeem also takes its code out of
 * the address bar straight away.
 */
// eslint-disable-next-line react-refresh/only-export-components -- shared with the test that runs the real posthog-js client
export const POSTHOG_PRIVACY_OPTIONS = {
  mask_personal_data_properties: true,
  custom_personal_data_properties: ["token", "code"],
};

interface PostHogProviderProps {
  children: ReactNode;
}

const PostHogReadyContext = createContext(false);

// eslint-disable-next-line react-refresh/only-export-components -- co-located consent-ready hook; splitting it would break no-runtime behavior only to satisfy fast refresh
export function usePostHogReady() {
  return useContext(PostHogReadyContext);
}

function syncPostHogConsent(
  // PostHogInterface covers both usePostHog() and the `loaded` callback's instance.
  posthog: PostHogInterface | ReturnType<typeof usePostHog>,
  consentStatus: ConsentStatus,
  analyticsConsent: boolean,
) {
  if (!posthog) {
    return;
  }

  if (consentStatus === "accepted" && analyticsConsent) {
    // Cookie consent is tracked in our own store, so avoid emitting an extra
    // "$opt_in" event every time the app boots with persisted consent.
    posthog.opt_in_capturing({ captureEventName: false });
    return;
  }

  posthog.opt_out_capturing();
}

/**
 * Manages PostHog consent based on the cookie consent store.
 * Subscribes to consent changes and updates PostHog opt-in/opt-out status accordingly.
 *
 * Note: persisted consent is applied synchronously in the `loaded` callback
 * (see PostHogProvider below) so that the initial pageview captured by
 * PostHogPageviewTracker is not silently dropped due to React effect ordering.
 * This component handles subsequent runtime consent changes only.
 */
function PostHogConsentManager({ children, isReady }: { children: ReactNode; isReady: boolean }) {
  const posthog = usePostHog();
  const hasHandledInitialConsent = useRef(false);
  // Use shallow comparison to avoid unnecessary re-renders from object selector
  const { analyticsConsent, consentStatus } = useCookieConsentStore(
    useShallow((state) => ({
      analyticsConsent: state.preferences.analytics,
      consentStatus: state.status,
    })),
  );

  useEffect(() => {
    if (!posthog || !isReady) return;

    // The persisted consent state is applied from PostHog's `loaded` callback so
    // the first pageview can wait for an initialized client. This effect only
    // handles user-driven consent changes after that point.
    if (!hasHandledInitialConsent.current) {
      hasHandledInitialConsent.current = true;
      return;
    }

    syncPostHogConsent(posthog, consentStatus, analyticsConsent);
  }, [posthog, analyticsConsent, consentStatus, isReady]);

  return <>{children}</>;
}

export function PostHogProvider({ children }: PostHogProviderProps) {
  const [isReady, setIsReady] = useState(false);

  if (!posthogKey) {
    return <>{children}</>;
  }

  return (
    <PHProvider
      apiKey={posthogKey}
      options={{
        api_host: posthogHost,
        capture_pageview: false,
        capture_pageleave: true,
        // Start with tracking disabled until user consents (GDPR compliance)
        opt_out_capturing_by_default: true,
        // Apply persisted consent synchronously on init so the first pageview
        // captured by PostHogPageviewTracker (which fires in a React effect)
        // is not silently dropped due to the default opt-out state.
        loaded: (ph) => {
          // Read persisted consent directly from localStorage because
          // Zustand's persist middleware hydrates asynchronously and
          // getState() would return defaults ("pending") at this point.
          try {
            const raw = localStorage.getItem("cookie-consent-storage");
            if (raw) {
              const { state } = JSON.parse(raw) as {
                state?: { status?: string; preferences?: { analytics?: boolean } };
              };
              syncPostHogConsent(
                ph,
                (state?.status as ConsentStatus) ?? "pending",
                state?.preferences?.analytics ?? false,
              );
            }
          } catch {
            // localStorage unavailable (Safari private browsing) or corrupt —
            // consent will be applied via PostHogConsentManager effect after hydration.
          }
          setIsReady(true);
        },
        // Autocapture allowlist: limit tracking to click/submit events on common
        // interactive elements. The css_selector_allowlist requires explicit opt-in
        // via data-ph-capture attribute (e.g., <div data-ph-capture>...</div>).
        autocapture: {
          dom_event_allowlist: ["click", "submit"],
          element_allowlist: ["a", "button", "form", "input", "select", "textarea"],
          css_selector_allowlist: ["[data-ph-capture]"],
        },
        disable_session_recording: true,
        ...POSTHOG_PRIVACY_OPTIONS,
      }}
    >
      <PostHogReadyContext.Provider value={isReady}>
        <PostHogConsentManager isReady={isReady}>{children}</PostHogConsentManager>
      </PostHogReadyContext.Provider>
    </PHProvider>
  );
}
